import React from 'react'
import { FileText, StickyNote, Upload, TrendingUp, AlertCircle, CheckCircle, Clock } from 'lucide-react'
import WorkPanelWrapper from '../common/WorkPanelWrapper'

interface CaseSummary {
  id: string
  caseId?: string // Optional for backward compatibility
  name: string
  type: string
  status: 'pending' | 'processing' | 'complete' | 'error'
  progress: number
  documents: number | Array<{ id: string; filename: string }>
  flags: number
  date: string
  summary?: {
    overview: string
    keyFindings: string[]
    recommendations: string[]
    nextSteps: string[]
  }
}

interface SummaryViewProps {
  caseSummary: CaseSummary
  onUploadDocument?: () => void
  onCreateNote?: () => void
}

const SummaryView: React.FC<SummaryViewProps> = ({
  caseSummary,
  onUploadDocument,
  onCreateNote
}) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-500" />
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete': return 'bg-green-100 text-green-800'
      case 'processing': return 'bg-blue-100 text-blue-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'error': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // If no summary exists yet, show empty state
  if (!caseSummary.summary) {
    return (
      <WorkPanelWrapper title={`Case Summary - ${caseSummary.name}`}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-lg">
            <div className="mb-6">
              <FileText className="w-20 h-20 mx-auto text-gray-300" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-700 mb-3">
              No Summary Available Yet
            </h2>
            <p className="text-gray-500 mb-8">
              Get started by uploading documents or creating notes for this case.
              Once you have documents, we'll generate a comprehensive summary.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={onUploadDocument}
                className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center space-x-2 font-medium shadow-sm transition-colors"
              >
                <Upload className="w-5 h-5" />
                <span>Upload Document</span>
              </button>
              <button
                onClick={onCreateNote}
                className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg flex items-center space-x-2 font-medium shadow-sm transition-colors"
              >
                <StickyNote className="w-5 h-5" />
                <span>Create Note</span>
              </button>
            </div>
          </div>
        </div>
      </WorkPanelWrapper>
    )
  }

  // Show full summary
  return (
    <WorkPanelWrapper title={`Case Summary - ${caseSummary.name}`}>
      <div className="space-y-6">
        {/* Case Header */}
        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{caseSummary.name}</h2>
              <p className="text-gray-600">{caseSummary.type}</p>
              <p className="text-sm text-gray-500 mt-1">Case #{caseSummary.caseId || caseSummary.id}</p>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon(caseSummary.status)}
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(caseSummary.status)}`}>
                {caseSummary.status}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Overall Progress</span>
              <span>{caseSummary.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-500 h-2 rounded-full transition-all"
                style={{ width: `${caseSummary.progress}%` }}
              />
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <FileText className="w-6 h-6 mx-auto text-gray-600 mb-1" />
              <p className="text-2xl font-bold text-gray-900">{Array.isArray(caseSummary.documents) ? caseSummary.documents.length : caseSummary.documents}</p>
              <p className="text-xs text-gray-600">Documents</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <AlertCircle className="w-6 h-6 mx-auto text-yellow-600 mb-1" />
              <p className="text-2xl font-bold text-gray-900">{caseSummary.flags}</p>
              <p className="text-xs text-gray-600">Flags</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <TrendingUp className="w-6 h-6 mx-auto text-green-600 mb-1" />
              <p className="text-2xl font-bold text-gray-900">{caseSummary.progress}%</p>
              <p className="text-xs text-gray-600">Complete</p>
            </div>
          </div>
        </div>

        {/* Overview */}
        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Overview</h3>
          <p className="text-gray-700 leading-relaxed">{caseSummary.summary.overview}</p>
        </div>

        {/* Key Findings */}
        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Findings</h3>
          <ul className="space-y-2">
            {caseSummary.summary.keyFindings.map((finding, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span className="text-gray-700">{finding}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Recommendations</h3>
          <div className="space-y-3">
            {caseSummary.summary.recommendations.map((rec, index) => (
              <div key={index} className="flex items-start p-3 bg-orange-50 rounded-lg">
                <span className="text-orange-600 font-semibold mr-2">{index + 1}.</span>
                <span className="text-gray-700">{rec}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Next Steps</h3>
          <div className="space-y-2">
            {caseSummary.summary.nextSteps.map((step, index) => (
              <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-semibold mr-3">
                  {index + 1}
                </div>
                <span className="text-gray-700">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </WorkPanelWrapper>
  )
}

export default SummaryView
export type { CaseSummary }
